import { useState } from "react";
import axios, { API } from "../api";

/**
 * Phase 29.B — Secret lock badge for chat messages.
 *
 * Renders a small lock icon below a chat bubble. If the message is already
 * secret, the lock is filled and tinted; tapping unlocks it. If not secret,
 * the lock shows as a faint outline; tapping locks it.
 *
 * Secret messages are excluded from share-nudges, tweet/snap drafting, and
 * person-learning aggregation — Aura's confirmation that something stays
 * between her and the user.
 *
 * Props
 * -----
 *  - messageId:  the chat_messages.id (omit → renders disabled stub)
 *  - isSecret:   current state
 *  - onChange:   (newSecretBool) => void  parent flips the local state
 */
export function SecretLockBadge({ messageId, isSecret, onChange }) {
  const [busy, setBusy] = useState(false);
  if (!messageId) return null;

  const toggle = async () => {
    setBusy(true);
    const next = !isSecret;
    try {
      await axios.patch(`${API}/chat/messages/${messageId}/secret`, { secret: next });
      onChange?.(next);
    } catch (e) {
      // Silent — the badge stays in its current state.
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      className={`secret-lock-badge ${isSecret ? "locked" : "unlocked"}`}
      data-testid={`secret-lock-${messageId}`}
      onClick={toggle}
      disabled={busy}
      title={isSecret ? "Locked — only Aura sees this. Tap to unlock." : "Tap to lock this between you and Aura."}
      aria-label={isSecret ? "Unlock this message" : "Lock this message as a secret"}
    >
      {isSecret ? "🔒 secret" : "🔓"}
    </button>
  );
}
